/**
 * DateRangePicker Component
 * 
 * Calendar popup for selecting date ranges, matching Retell AI's design.
 * Shows presets on the left (Today, Last 7 days, etc.) and calendar on the right.
 */

import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth, isSameDay, isToday } from 'date-fns';

/**
 * DateRangePicker component
 * 
 * @param {object} props - Component props
 * @param {Date} props.startDate - Selected start date
 * @param {Date} props.endDate - Selected end date
 * @param {Function} props.onChange - Callback when date range changes
 * @param {Function} props.onClose - Callback when picker should close
 */
const DateRangePicker = ({ startDate, endDate, onChange, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  /**
   * Preset date ranges (left sidebar)
   */
  const presets = [
    { id: 'today', label: 'Today' },
    { id: 'last_7_days', label: 'Last 7 days' },
    { id: 'last_4_weeks', label: 'Last 4 weeks' },
    { id: 'last_3_months', label: 'Last 3 months' },
    { id: 'week_to_date', label: 'Week to date' },
    { id: 'month_to_date', label: 'Month to date' },
    { id: 'year_to_date', label: 'Year to date' },
    { id: 'all_time', label: 'All time' },
  ];

  /**
   * Handle preset click
   */
  const handlePresetClick = (presetId) => {
    const now = new Date();
    let start, end = now;

    switch (presetId) {
      case 'today':
        start = new Date();
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'last_7_days':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'last_4_weeks':
        start = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'last_3_months':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'week_to_date':
        start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'month_to_date':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'year_to_date':
        start = new Date(now.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date();
        end.setHours(23, 59, 59, 999);
        break;
      case 'all_time':
        // For "All time", apply immediately without requiring the Apply button
        onChange(null, null, 'all_time');
        onClose();
        return;
      default:
        return;
    }

    // Update the temporary dates to show in the calendar
    setTempStartDate(start);
    setTempEndDate(end);
    // Update the calendar month to show the start date
    if (start) {
      setCurrentMonth(start);
    }
  };

  /**
   * Handle date click in calendar
   */
  const handleDateClick = (date) => {
    if (selectingStart) {
      setTempStartDate(date);
      setTempEndDate(null);
      setSelectingStart(false);
    } else {
      if (date < tempStartDate) {
        setTempStartDate(date);
        setTempEndDate(tempStartDate);
      } else {
        setTempEndDate(date);
      }
      setSelectingStart(true);
    }
  };

  /**
   * Handle Apply button
   */
  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      console.log('📅 DateRangePicker - Applying:',
        'start:', tempStartDate?.toISOString?.(),
        '| end:', tempEndDate?.toISOString?.(),
        '| startTs:', tempStartDate?.getTime?.(),
        '| endTs:', tempEndDate?.getTime?.()
      );
      onChange(tempStartDate, tempEndDate, 'custom');
      onClose();
    }
  };

  /**
   * Render calendar days for a specific month
   */
  const renderCalendar = (monthToRender) => {
    const monthStart = startOfMonth(monthToRender);
    const monthEnd = endOfMonth(monthToRender);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const formattedDate = format(day, 'd');
        const cloneDay = day;
        
        const isSelected = (tempStartDate && isSameDay(day, tempStartDate)) || 
                          (tempEndDate && isSameDay(day, tempEndDate));
        const isInRange = tempStartDate && tempEndDate && 
                         day >= tempStartDate && day <= tempEndDate;
        const isCurrentMonth = isSameMonth(day, monthToRender);
        const isTodayDate = isToday(day);

        days.push(
          <button
            key={day}
            onClick={() => handleDateClick(cloneDay)}
            className={`
              h-9 w-9 p-0 rounded-sm text-sm font-normal transition-colors
              ${!isCurrentMonth ? 'text-muted-foreground opacity-50' : 'text-neutral-900'}
              ${isSelected ? 'bg-primary-600 text-white' : ''}
              ${isInRange && !isSelected ? 'bg-primary-50 text-primary-900' : ''}
              ${!isSelected && !isInRange && isCurrentMonth ? 'hover:bg-neutral-100' : ''}
              ${isTodayDate && !isSelected ? 'bg-accent text-accent-foreground' : ''}
            `}
          >
            {formattedDate}
          </button>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day} className="flex w-full mt-2">
          {days}
        </div>
      );
      days = [];
    }
    return rows;
  };

  return (
    <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-neutral-200 z-50 flex">
      {/* Left sidebar - Presets */}
      <div className="w-48 border-r border-neutral-200 p-4">
        {presets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => handlePresetClick(preset.id)}
            className="w-full text-left px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100 rounded-md transition-colors"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Right side - Calendar */}
      <div className="p-3 flex flex-col">
        {/* Two months side by side */}
        <div className="flex flex-row pb-0">
          {/* First month */}
          <div className="space-y-4 border-r pr-4">
            {/* Month header with navigation */}
            <div className="flex justify-center pt-1 relative items-center">
              <h3 className="text-sm font-medium text-neutral-900">
                {format(currentMonth, 'MMMM yyyy')}
              </h3>
              <div className="flex items-center absolute scale-75 -left-6">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  className="h-7 w-7 bg-transparent p-0 border border-neutral-400 hover:bg-neutral-100 rounded-md hover:border-neutral-600 transition-colors text-neutral-900"
                >
                  <svg className="w-4 h-4 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Calendar table */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="flex">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <th key={day} className="text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]">
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderCalendar(currentMonth)}
              </tbody>
            </table>
          </div>

          {/* Second month */}
          <div className="space-y-4 pl-4">
            {/* Month header with navigation */}
            <div className="flex justify-center pt-1 relative items-center">
              <h3 className="text-sm font-medium text-neutral-900">
                {format(addMonths(currentMonth, 1), 'MMMM yyyy')}
              </h3>
              <div className="flex items-center absolute scale-75 -right-6">
                <button
                  onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  className="h-7 w-7 bg-transparent p-0 border border-neutral-400 hover:bg-neutral-100 rounded-md hover:border-neutral-600 transition-colors text-neutral-900"
                >
                  <svg className="w-4 h-4 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Calendar table */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="flex">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                    <th key={day} className="text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]">
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderCalendar(addMonths(currentMonth, 1))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-200">
          <div className="text-sm text-neutral-600">
            Range: {tempStartDate && tempEndDate ? 
              `${format(tempStartDate, 'MMM d')} - ${format(tempEndDate, 'MMM d')}` : 
              'Select dates'}
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={!tempStartDate || !tempEndDate}
              className="px-4 py-2 text-sm font-medium text-white bg-neutral-900 hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;

